import { createAsyncThunk } from "@reduxjs/toolkit";

import { type NotificationType } from "@fitness-app/data-models/entities/Notification";

import { type AsyncThunkCreator } from "../../../index";
import { NOTIFICATIONS_REDUCER_NAME } from "../types";
import { fetchNotifications } from "./fetchNotifications";

// TRE-1823: this used to be a single UPDATE covering every unread row. On an account with a backlog
// (359k rows on the owner) that statement always hit statement_timeout = 30s and, being atomic,
// rolled back — the badge blinked to 0 and every notification came back unread. The RPC marks a
// bounded batch instead, so each statement stays far below the timeout and we loop until it reports
// a batch shorter than requested.
const BATCH_SIZE = 5000;
// ~360k unread is ~72 batches; the cap only exists so a bug upstream cannot spin forever.
const MAX_BATCHES = 200;
// Every marked row is still a WAL record that Realtime fans out — pace the batches so clearing a
// large backlog does not dump the whole thing into the replication stream at once.
const PAUSE_BETWEEN_BATCHES_MS = 150;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface BatchOptions {
  batchSize?: number;
  maxBatches?: number;
  pauseMs?: number;
  pause?: (ms: number) => Promise<void>;
}

/**
 * Calls `markBatch` until it marks fewer rows than it was asked for (nothing left to mark) or the
 * batch cap is reached. `completed: false` means the caller stopped early and rows may remain.
 */
export const markAllInBatches = async (
  markBatch: (batchSize: number) => Promise<number>,
  {
    batchSize = BATCH_SIZE,
    maxBatches = MAX_BATCHES,
    pauseMs = PAUSE_BETWEEN_BATCHES_MS,
    pause = wait,
  }: BatchOptions = {},
): Promise<{ marked: number; completed: boolean }> => {
  let marked = 0;

  for (let batch = 0; batch < maxBatches; batch++) {
    const markedInBatch = await markBatch(batchSize);
    marked += markedInBatch;

    if (markedInBatch < batchSize) {
      return { marked, completed: true };
    }

    await pause(pauseMs);
  }

  return { marked, completed: false };
};

type Payload = { type?: NotificationType[] } | void;

export const markAllAsRead = createAsyncThunk<
  { marked: number; completed: boolean },
  Payload,
  AsyncThunkCreator<string>
>(
  `${NOTIFICATIONS_REDUCER_NAME}/markAllAsRead`,
  async (payload, { dispatch, getState, rejectWithValue, extra: { db, analytics, errorTrackingService } }) => {
    const { filter } = getState().notifications;

    try {
      const { marked, completed } = await markAllInBatches(async (batchSize) => {
        const { data, error } = await db.rpc("mark_all_notifications_as_read", { p_batch_size: batchSize });

        if (error) {
          throw error;
        }

        return data ?? 0;
      });

      analytics.track("mark_all_notifications_as_read", { marked, completed });

      return { marked, completed };
    } catch (error) {
      errorTrackingService?.recordError(error, "markAllAsRead action");

      return rejectWithValue(error instanceof Error ? error.message : String(error));
    } finally {
      // Whichever way it went — done, stopped early or failed — the list and the counter are read
      // back from the database, so the optimistic zero is never left standing in for the truth.
      await dispatch(fetchNotifications({ filter, type: payload?.type }));
    }
  },
);
